import type { AxleListing } from '@contracts/axle/AxleListing';
import type { TrailerRequest } from '@contracts/trailer/TrailerRequest';
import type { TrailerWithAxlesResponse } from '@contracts/trailer/TrailerWithAxlesResponse';
import { VehicleAxlesList } from '@src/axles/domain/VehicleAxlesList';
import type { AxleFormInput } from '@src/tractor/AxleFormInput';
import type { TrailerListing } from '@src/trailer/domain/TrailerList';
import { TrailerList } from '@src/trailer/domain/TrailerList';
import { TrailerClient } from '@src/trailer/service/TrailerClient';
import type { TrailerFormSchema } from '@src/trailer/TrailerFormSchema';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type * as yup from 'yup';

interface Props {
  clientId: string;
  trailer: TrailerListing;
  onSuccess: (newTrailer: TrailerWithAxlesResponse) => void;
  onError: () => void;
}

export const useUpdateTrailer = ({
  clientId,
  trailer,
  onSuccess,
  onError,
}: Props) => {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: (data: yup.InferType<typeof TrailerFormSchema>) => {
      const dataAxles = data.axles
        ? (data.axles as unknown as yup.InferType<typeof AxleFormInput>[])
        : [];

      return TrailerClient.update(clientId, trailer.id, {
        unitNumber: data.trailerUnitNumber,
        make: data.trailerMake,
        type: data.trailerType,
        vin: data.trailerVIN,
        year: data.trailerYear,
        licensePlateNumber: data.trailerLicensePlate,
        licensePlateState: data.trailerLicensePlateState,
        licensePlateType: data.trailerLicensePlateType,
        axles: dataAxles.map((axle, i) => {
          const id = trailer.axles[i]?.id.length
            ? trailer.axles[i]?.id
            : undefined;
          return {
            weight: axle.axleWeight ?? undefined,
            capacity: axle.axleCapacity ?? undefined,
            width: axle.axleWidth ?? undefined,
            tireCount: axle.tireCount ?? undefined,
            tireRating: axle.tireRating ?? undefined,
            tireSize: axle.tireSize?.toString().length
              ? axle.tireSize
              : undefined,
            suspensionType: axle.suspensionType?.toString().length
              ? axle.suspensionType
              : undefined,
            group: axle.axleGroup?.toString().length
              ? axle.axleGroup
              : undefined,
            isSteeringAxle: axle.isSteeringAxle ?? undefined,
            spacing: axle.axleSpacing ?? undefined,
            type: axle.axleType?.toString().length ? axle.axleType : undefined,
            id,
          } as AxleListing;
        }),
        length: data.trailerLength,
        width: data.trailerWidth,
        height: data.trailerHeight,
        wheelbaseLength: data.trailerWheelbaseLength,
        deckHeight: data.trailerDeckHeight,
        groundClearance: data.trailerGroundClearance,
        kingpinSetbackLength: data.trailerKingpinSetbackLength,
        kingpinSettingLength: data.trailerKingpinSettingLength,
        emptyWeight: data.trailerEmptyWeight,
        registeredWeight: data.trailerRegisteredWeight,
      } as TrailerRequest);
    },
    onSuccess: async (updatedTrailer) => {
      onSuccess(updatedTrailer);
      queryClient.setQueryData(
        ['trailers.list', clientId],
        (prev: TrailerList | undefined) => {
          if (!prev) {
            return new TrailerList([updatedTrailer]);
          }
          return new TrailerList([
            ...prev.trailers.filter((t) => t.id !== updatedTrailer.id),
            updatedTrailer,
          ]);
        },
      );
      queryClient.setQueryData(
        ['axles.list', clientId],
        (prev: VehicleAxlesList | undefined) => {
          if (!prev) {
            return new VehicleAxlesList({
              trailersAxles: [
                { vehicleId: updatedTrailer.id, axles: updatedTrailer.axles },
              ],
              tractorsAxles: [],
            });
          }
          return new VehicleAxlesList({
            trailersAxles: [
              { vehicleId: updatedTrailer.id, axles: updatedTrailer.axles },
            ],
            tractorsAxles: prev.tractorsAxles,
          });
        },
      );
      await queryClient.invalidateQueries({
        queryKey: ['trailers.list', clientId],
      });
      await queryClient.invalidateQueries({
        queryKey: ['trailers.get', clientId, trailer.id],
      });
      await queryClient.invalidateQueries({
        queryKey: ['axles.list', clientId],
      });
    },
    onError,
  });

  return {
    updateTrailer: mutate,
    isLoading: isPending,
  };
};
